import { useLayoutEffect, useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { addMonths, startOfDay } from 'date-fns'
import { useLocation } from 'react-router-dom'

import { CourseSummaryCard } from '@/components/public/course-summary-card'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { getPublicCourseCatalog, getPublicSessions } from '@/lib/api'
import { queryKeys } from '@/lib/query-keys'
import { restoreCoursesOverviewScrollTop } from '@/lib/scroll-container'
import type { PublicCourseCatalogCourse, PublicSession } from '@/types/domain'

type CourseCatalogEntry = {
  course: PublicCourseCatalogCourse
  nextSession: PublicSession | null
  trainerName: string
}

type CoursesPageLocationState = {
  restoreCoursesOverview?: boolean
}

const LEADING_EMOJI_PATTERN = /^[\p{White_Space}\p{Extended_Pictographic}\uFE0F\u200D]+/u

export function CoursesPage() {
  const location = useLocation()
  const hasRestoredScrollRef = useRef(false)
  const routeState = location.state as CoursesPageLocationState | null
  const today = useMemo(() => startOfDay(new Date()), [])
  const nextYear = useMemo(() => addMonths(today, 12), [today])

  const coursesQuery = useQuery({
    queryKey: queryKeys.publicCourseCatalog,
    queryFn: getPublicCourseCatalog,
  })

  const sessionsQuery = useQuery({
    queryKey: queryKeys.publicSessions(today.toISOString(), nextYear.toISOString()),
    queryFn: () => getPublicSessions(today.toISOString(), nextYear.toISOString()),
  })

  const courseEntries = useMemo<CourseCatalogEntry[]>(() => {
    const sessionsByCourse = new Map<string, PublicSession[]>()

    for (const session of sessionsQuery.data ?? []) {
      const list = sessionsByCourse.get(session.course_id) ?? []
      list.push(session)
      sessionsByCourse.set(session.course_id, list)
    }

    return (coursesQuery.data ?? [])
      .map((course) => {
        const upcomingSessions = (sessionsByCourse.get(course.id) ?? []).sort(
          (a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
        )

        return {
          course,
          nextSession: upcomingSessions[0] ?? null,
          trainerName: resolveTrainerName(course),
        }
      })
      .sort((a, b) =>
        normalizeCourseSortName(a.course.name).localeCompare(
          normalizeCourseSortName(b.course.name),
          'de',
          { sensitivity: 'base' },
        ),
      )
  }, [coursesQuery.data, sessionsQuery.data])

  const isLoading = coursesQuery.isLoading || sessionsQuery.isLoading
  const hasError = coursesQuery.isError || sessionsQuery.isError

  useLayoutEffect(() => {
    if (hasRestoredScrollRef.current || isLoading || hasError || !routeState?.restoreCoursesOverview) {
      return
    }

    hasRestoredScrollRef.current = true
    restoreCoursesOverviewScrollTop()
  }, [hasError, isLoading, routeState?.restoreCoursesOverview])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Kurse</h1>
        <p className="text-sm text-muted-foreground">Alle Kurse und ihre nächsten Termine</p>
      </div>

      {isLoading && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Card key={index} className="overflow-hidden">
              <Skeleton className="aspect-[4/3] w-full" />
              <CardContent className="space-y-4 p-5">
                <Skeleton className="h-6 w-2/3" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {hasError && (
        <Card>
          <CardContent className="p-6 text-sm text-destructive">
            Kursdaten konnten nicht geladen werden.
          </CardContent>
        </Card>
      )}

      {!isLoading && !hasError && courseEntries.length === 0 && (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Aktuell sind keine Kurse verfügbar.
          </CardContent>
        </Card>
      )}

      {!isLoading && !hasError && courseEntries.length > 0 && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {courseEntries.map((entry) => (
            <CourseSummaryCard
              key={entry.course.id}
              course={entry.course}
              trainerName={entry.trainerName}
              to={`/kurse/${entry.course.id}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function resolveTrainerName(course: PublicCourseCatalogCourse) {
  const courseTrainerNames = course.trainer_names?.trim()
  if (courseTrainerNames) {
    return courseTrainerNames
  }

  return 'Trainer ausständig'
}

function normalizeCourseSortName(name: string) {
  const normalizedName = name.normalize('NFKC').replace(LEADING_EMOJI_PATTERN, '').trim()
  return normalizedName || name.trim()
}
